import {runAsync, sleep} from "@prosay/common"
import {ReactNodeProps, useSnackbarMessage} from "@prosay/components"
import {translate} from "@prosay/i18n"
import {getLogger} from "@prosay/logging"
import React, {useCallback} from "react"
import {Clipboard, StyleSheet, View} from "react-native"
import {ActivityIndicator, List} from "react-native-paper"
import {Actions} from "react-native-router-flux"
import {Icons} from "../../constants"
import {useCurrentUserQuery, useLogoutMutation} from "../../graphql"
import {getMyRefreshToken, logout} from "../../util"

const logger = getLogger(__filename)

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
})

export interface ProfileListItemProps {
    description?: string
    icon?: string
    onPress: () => void | Promise<void>
    style?: ReactNodeProps<typeof List.Item>["style"]
    title: string
}
export const ProfileListItem: React.FC<ProfileListItemProps> = ({
    description,
    icon,
    onPress,
    style,
    title,
}) => {
    const onPressVoid = useCallback(
        () => runAsync(async () => await Promise.resolve(onPress())),
        [onPress],
    )

    return (
        <List.Item
            style={style}
            title={title}
            description={description}
            onPress={onPressVoid}
            left={
                icon ? props => <List.Icon {...props} icon={icon} /> : undefined
            }
        />
    )
}

export interface LogoutProps {}
export const Logout: React.FC<LogoutProps> = () => {
    const [mutate] = useLogoutMutation()
    const sendMessage = useSnackbarMessage()
    const onPress = useCallback(async () => {
        const token = await getMyRefreshToken()
        if (!token) {
            logger.warn`Stored refresh token was null. Ignoring logout mutation.`
            return
        }

        await mutate({variables: {token}})
        await logout()

        sendMessage(translate("ui.screen.profile.loggedOutMessage"))
        await sleep(50)
        Actions.register()
    }, [mutate, sendMessage])

    return <ProfileListItem onPress={onPress} title="Logout" icon="exit-run" />
}

const useCopy = (data: string | undefined) => {
    const sendMessage = useSnackbarMessage()
    return useCallback(() => {
        if (data) {
            Clipboard.setString(data)
            sendMessage(
                translate("ui.screen.profile.copiedToClipboardMessage", {data}),
            )
        }
    }, [data, sendMessage])
}

export interface AccountInformationProps {}
export const AccountInformation: React.FC<AccountInformationProps> = () => {
    const {data, loading} = useCurrentUserQuery()
    const copyId = useCopy(data?.currentUser?.user.id)
    const copyPhoneNumber = useCopy(data?.currentUser?.phoneNumber)

    if (loading) {
        return <ActivityIndicator />
    }

    return (
        <List.Section>
            <List.Subheader>
                {translate("ui.screen.profile.menu.accountInformationTitle")}
            </List.Subheader>
            <List.Item
                title={translate("ui.screen.profile.menu.accountType")}
                description={
                    data?.currentUser?.user.__typename === "Client"
                        ? translate("user.client")
                        : translate("user.lawyer")
                }
                left={props => (
                    <List.Icon
                        {...props}
                        icon={
                            data?.currentUser?.user.__typename === "Lawyer"
                                ? Icons.lawyer
                                : Icons.client
                        }
                    />
                )}
            />
            <List.Item
                title={translate("ui.screen.profile.menu.userId")}
                description={data?.currentUser?.user.id}
                onPress={copyId}
                left={props => <List.Icon {...props} icon="account" />}
            />
            <List.Item
                title={translate("ui.screen.profile.menu.phoneNumber")}
                description={data?.currentUser?.phoneNumber}
                onPress={copyPhoneNumber}
                left={props => <List.Icon {...props} icon="phone" />}
            />
        </List.Section>
    )
}

// TODO: Add change phone number feature
export interface ProfileProps {}
export const Profile: React.FC<ProfileProps> = () => {
    return (
        <View style={styles.container}>
            <AccountInformation />
            <List.Section>
                <List.Subheader>
                    {translate("ui.screen.profile.menu.accountSettingsTitle")}
                </List.Subheader>
                <Logout />
            </List.Section>
        </View>
    )
}
